import { validatePaymentRequest, validateAccountingCodes } from '../../utils/formValidation';

interface ValidationRule {
  type: string;
  message: string;
  validate: (value: any) => boolean;
}

export const validators = {
  required: (message = 'This field is required'): ValidationRule => ({
    type: 'required',
    message,
    validate: (value: any) => {
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined;
    }
  }),

  email: (message = 'Invalid email address'): ValidationRule => ({
    type: 'email',
    message,
    validate: (value: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    }
  }),

  minLength: (length: number, message = `Minimum length is ${length}`): ValidationRule => ({
    type: 'minLength',
    message,
    validate: (value: string) => value.length >= length
  }),

  maxLength: (length: number, message = `Maximum length is ${length}`): ValidationRule => ({
    type: 'maxLength',
    message,
    validate: (value: string) => value.length <= length
  }),

  pattern: (regex: RegExp, message = 'Invalid format'): ValidationRule => ({
    type: 'pattern',
    message,
    validate: (value: string) => regex.test(value)
  }),

  numeric: (message = 'Must be a number'): ValidationRule => ({
    type: 'numeric',
    message,
    validate: (value: any) => !isNaN(parseFloat(value)) && isFinite(value)
  }),

  date: (message = 'Invalid date'): ValidationRule => ({
    type: 'date',
    message,
    validate: (value: any) => !isNaN(Date.parse(value))
  }),

  custom: (validateFn: (value: any) => boolean, message: string): ValidationRule => ({
    type: 'custom',
    message,
    validate: validateFn
  }),

  form990: {
    ein: (message = 'EIN must be in XX-XXXXXXX format'): ValidationRule => ({
      type: 'ein',
      message,
      validate: (value: string) => /^\d{2}-\d{7}$/.test(value)
    }),

    revenue: (totalRevenue: number | undefined, message = 'Revenue calculations do not match'): ValidationRule => ({
      type: 'revenue',
      message,
      validate: (calculatedRevenue: number | undefined) => {
        if (totalRevenue === undefined || calculatedRevenue === undefined) {
          return true;
        }
        return Math.abs(calculatedRevenue - totalRevenue) < 1;
      }
    }),

    balanceSheet: (message = 'Balance sheet validation failed'): ValidationRule => ({
      type: 'balanceSheet',
      message,
      validate: (data: any) => {
        if (!data?.part_x?.assets?.end) {
          return true;
        }

        const assets = data.part_x.assets.end.total || 0;
        const liabilities = data.part_x.liabilities?.end?.total || 0;
        const netAssets = data.part_x.net_assets?.end?.total || 0;

        if (assets === 0 && liabilities === 0 && netAssets === 0) {
          return true;
        }

        return Math.abs(assets - (liabilities + netAssets)) < 1;
      }
    }),

    xfaField: (fieldName: string, message = `Invalid value for ${fieldName}`): ValidationRule => ({
      type: 'xfaField',
      message,
      validate: (value: any) => {
        const fieldType = Form990XFAFields.get(fieldName)?.type;
        switch (fieldType) {
          case 'currency':
            return !isNaN(parseFloat(value)) && value >= 0;
          case 'checkbox':
            return typeof value === 'boolean';
          case 'date':
            return !isNaN(Date.parse(value));
          default:
            return true;
        }
      }
    }),

    validateForm990: (formData: any): Record<string, string> => {
      const data = formData.data || formData;
      
      const validationRules: Record<string, ValidationRule[]> = {
        'header.ein': data?.header?.ein ? [validators.form990.ein()] : [],
        'revenue.total': data?.revenue?.total !== undefined ? 
          [validators.form990.revenue(data.revenue.total)] : [],
        'balanceSheet': [validators.form990.balanceSheet()]
      };

      Form990XFAFields.forEach((fieldInfo, fieldPath) => {
        const value = form990PDFUtils.getFieldValue(data, fieldPath);
        if (value !== undefined) {
          validationRules[fieldPath] = [
            validators.form990.xfaField(fieldPath)
          ];
        }
      });

      return validateForm(data, validationRules);
    }
  }
};

export const validateForm = (formData: any, validationRules: Record<string, ValidationRule[]>) => {
  const errors: Record<string, string> = {};

  Object.entries(validationRules).forEach(([field, rules]) => {
    const value = formData[field];
    
    for (const rule of rules) {
      if (!rule.validate(value)) {
        errors[field] = rule.message;
        break;
      }
    }
  });

  return errors;
};

// Re-export existing validation functions
export { validatePaymentRequest, validateAccountingCodes }; 

// Add Form 990 XFA field definitions
export const Form990XFAFields = new Map([
  ['header.name', { type: 'text', field: 'topmostSubform[0].Page1[0].f1_1[0]' }],
  ['header.ein', { type: 'ein', field: 'topmostSubform[0].Page1[0].f1_2[0]' }],
  ['revenue.total', { type: 'currency', field: 'topmostSubform[0].Page1[0].f1_12[0]' }],
  // ... add all your XFA field mappings
]);

// Add Form 990 specific validation function
export const validateForm990 = (formData: any): Record<string, string> => {
  const validationRules: Record<string, ValidationRule[]> = {
    'header.ein': [validators.form990.ein()],
    'revenue.total': [validators.form990.revenue(formData.revenue.total)],
    'balanceSheet': [validators.form990.balanceSheet()]
  };

  // Add XFA field validations
  Form990XFAFields.forEach((fieldInfo, fieldPath) => {
    validationRules[fieldPath] = [
      validators.form990.xfaField(fieldPath),
      validators.required(`${fieldPath} is required`)
    ];
  });

  return validateForm(formData, validationRules);
};

// Add Form 990 PDF handling utilities
export const form990PDFUtils = {
  getFieldValue: (data: any, fieldPath: string): any => {
    return fieldPath.split('.').reduce((obj, key) => obj?.[key], data);
  },

  setFieldValue: async (form: any, fieldName: string, value: any): Promise<void> => {
    const field = form.getField(fieldName);
    if (!field) return;

    const fieldInfo = Form990XFAFields.get(fieldName);
    switch (fieldInfo?.type) {
      case 'currency':
        field.setText(value.toFixed(2));
        break;
      case 'checkbox':
        value ? field.check() : field.uncheck();
        break;
      case 'date':
        field.setText(new Date(value).toLocaleDateString());
        break;
      default:
        field.setText(String(value));
    }
  }
}; 